var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useAxiosGet } from '../hooks';
import { LoadingSpinner } from '../common/loading-spinner';
import { useTranslation } from 'react-i18next';
import { PregradingExamUrlsContext } from './pregrading';
export const AnswersUploadPanel = ({ schoolId, isPrincipal, loadHeldExams }) => {
    const pregradingExamUrls = useContext(PregradingExamUrlsContext);
    const uploadRef = useRef(null);
    const [uploadError, setUploadError] = useState(null);
    const [uploadResult, setUploadResult] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [get] = useAxiosGet();
    const { t } = useTranslation();
    const [unmappedStudents, setUnmappedStudents] = useState([]);
    useEffect(() => void loadUnmappedStudents(schoolId), [isPrincipal, schoolId]);
    const handleClick = (e) => {
        var _a;
        e.preventDefault();
        (_a = uploadRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    const handleFile = (e) => {
        var _a;
        const file = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (!file || file.name.trim() === '')
            return;
        setUploading(true);
        setUploadError(null);
        setUploadResult([]);
        const formData = new FormData();
        formData.append('examUpload', file);
        axios
            .post(pregradingExamUrls.uploadAnswers(schoolId), formData, {
            headers: {
                enctype: 'multipart/form-data'
            }
        })
            .then((response) => __awaiter(void 0, void 0, void 0, function* () {
            setUploadResult(response.data);
            yield loadHeldExams(schoolId);
            yield loadUnmappedStudents(schoolId);
            return;
        }))
            .catch((error) => {
            setUploadError(getUploadErrorMessageKey(error));
        })
            .finally(() => {
            if (uploadRef.current) {
                uploadRef.current.value = '';
            }
            setUploading(false);
        });
    };
    function loadUnmappedStudents(schoolId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isPrincipal)
                return setUnmappedStudents([]);
            const data = yield get(pregradingExamUrls.unmappedStudents(schoolId));
            if (data) {
                setUnmappedStudents(data);
            }
        });
    }
    return (React.createElement("div", { id: "answers-upload-main-panel" },
        React.createElement("div", { id: "answers-upload" },
            React.createElement(LoadingSpinner, { loading: uploading }),
            React.createElement("form", { id: "answers-upload-panel", name: "answers-upload", method: "POST", action: "" },
                React.createElement("p", { className: "import-answers-instructions" }, t('sa.import_answers_instructions')),
                React.createElement("div", { className: "answers-upload-button-wrapper" },
                    React.createElement("label", { htmlFor: "examUpload" },
                        React.createElement("button", { className: "upload-button", disabled: uploading, onClick: e => handleClick(e), tabIndex: 0 }, t('sa.import_answers'))),
                    uploading && (React.createElement("div", { className: "upload-takes-long-container" },
                        React.createElement("span", { className: "upload-spinner" }),
                        React.createElement("span", { className: "notification-text" }, t('sa.answer_upload_takes_time'))))),
                React.createElement("input", { id: "examUpload", className: "upload-input", ref: uploadRef, type: "file", name: "examUpload", accept: ".meb", onChange: e => handleFile(e) }),
                uploadError && React.createElement("div", { className: "upload-error error-notice" }, t(uploadError)),
                uploadResult.length > 0 && React.createElement(UploadNotification, { schools: uploadResult }))),
        unmappedStudents.length > 0 && React.createElement(UnMappedStudents, { students: unmappedStudents })));
};
function getUploadErrorMessageKey(error) {
    var _a, _b;
    const data = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data;
    switch ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) {
        case 422:
            return 'sa.errors.no_answer_papers';
        case 428:
            return 'sa.errors.grading_started';
        case 415:
            return 'sa.errors.exam_meb';
        case 409:
            return `sa.errors.${data.errorCode || 'incorrect_exam'}`;
        case 400:
            return 'sa.errors.invalid_file';
        default:
            return 'sa.errors.answer_upload_failed';
    }
}
const UploadNotification = ({ schools }) => {
    const { t } = useTranslation();
    const divRef = useRef(null);
    const [styles, setStyles] = useState({ opacity: 0, maxHeight: '0', overflow: 'hidden' });
    const count = schools
        .filter(school => school.gradingSchoolId == null)
        .flatMap(school => school.answerCountsByExam)
        .reduce((sum, e) => sum + e.answerCount, 0);
    const filteredSchools = schools.filter(school => school.gradingSchoolId != null);
    useEffect(() => {
        var _a, _b;
        const scrollHeight = (_b = (_a = divRef.current) === null || _a === void 0 ? void 0 : _a.scrollHeight) !== null && _b !== void 0 ? _b : 0;
        setStyles(prev => (Object.assign(Object.assign({}, prev), { maxHeight: `${scrollHeight}px` })));
        setTimeout(() => {
            setStyles(prev => (Object.assign(Object.assign({}, prev), { opacity: 1, transition: 'opacity 1s, max-height 1s' // Transition effects
             })));
        }, 100);
    }, []);
    const handleCloseClick = (e) => {
        e.preventDefault();
        setStyles(prev => (Object.assign(Object.assign({}, prev), { opacity: 0, maxHeight: '0' })));
    };
    // ugly hack to decide whether or not to show answer-visibility-info (isDuplicate is defined)
    const answersVisibilityInfo = filteredSchools.some(school => school.answerCountsByExam.some(exam => exam.isDuplicate != undefined));
    return (React.createElement("div", { className: "answers-uploaded-notification", ref: divRef, style: Object.assign(Object.assign({}, styles), { transition: 'max-height 0.1s ease-out, opacity 0.1s ease-in' }) },
        React.createElement("div", { className: "upload-notification-title" }, t('sa.answers_uploaded.title')),
        count > 0 && React.createElement("div", { className: "without-school" }, t('sa.answers_uploaded.no_registration', { count })),
        React.createElement("table", null, filteredSchools.map(({ gradingSchoolId, gradingSchoolName, answerCountsByExam }) => (React.createElement("tbody", { key: gradingSchoolId },
            React.createElement("tr", { className: "school-title" },
                React.createElement("td", { colSpan: 2 },
                    gradingSchoolName ? gradingSchoolName : React.createElement("span", null, t('sa.answers_uploaded.unknown_school')),
                    ":")),
            answerCountsByExam.map(({ examUuid, examTitle, answerCount, isDeleted, isDuplicate }) => (React.createElement(Fragment, { key: examUuid },
                React.createElement("tr", null,
                    React.createElement("td", { className: "exam-title" }, examTitle),
                    React.createElement("td", { className: "answer-count" }, answerCount)),
                (isDeleted || isDuplicate) && (React.createElement("tr", null,
                    React.createElement("td", { colSpan: 2 },
                        isDuplicate && (React.createElement("span", { className: "error-notice" }, t('sa.answers_uploaded.answers_uploaded_already'))),
                        isDeleted && (React.createElement("div", { className: "js-answers-uploaded-to-deleted", dangerouslySetInnerHTML: {
                                __html: t('sa.answers_uploaded.answers_uploaded_to_deleted_instructions')
                            } })))))))))))),
        answersVisibilityInfo && (React.createElement("div", { className: "answers-visibility-info" },
            " ",
            t('sa.answers_uploaded.answers_visibility_info'))),
        React.createElement("button", { id: "answers-uploaded-close", onClick: handleCloseClick }, t('sa.answers_uploaded.close')),
        React.createElement("div", { className: "clear-both" })));
};
const UnMappedStudents = ({ students }) => {
    const { t } = useTranslation();
    return (React.createElement("div", { className: "unmapped-students js-unmapped-students" },
        React.createElement("h3", null, t('sa.unmapped_students.title')),
        React.createElement("table", { className: "basic-table" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, t('sa.unmapped_students.name')),
                    React.createElement("th", null, t('sa.unmapped_students.ssn')),
                    React.createElement("th", null, t('sa.unmapped_students.exam')))),
            React.createElement("tbody", null, students.map(({ lastName, firstNames, ssn, examTitles }) => (React.createElement("tr", { key: ssn },
                React.createElement("td", null,
                    lastName,
                    ", ",
                    firstNames),
                React.createElement("td", null, ssn),
                React.createElement("td", null, examTitles.map((title, index) => (React.createElement("div", { key: index }, title))))))))),
        React.createElement("p", { className: "js-unmapped-students-instructions", dangerouslySetInnerHTML: { __html: t('sa.unmapped_students.instructions') } })));
};
//# sourceMappingURL=answers-upload-panel.js.map